import pickBy from 'lodash/fp/pickBy';
import zip from 'lodash/fp/zip';
import flatten from 'lodash/fp/flatten';
import compose from 'lodash/fp/compose';

import config from '../utils/constants/config';
import { GeocodingFeatureCollection } from '../types/mapbox-search-api-v1.types';

const clean = pickBy((v) => v !== null && v !== undefined);

/** interlace([1, 2, 3], ['a', 'b', 'c']) -> [1, 'a', 2, 'b', 3, 'c'] */
const interlace = compose(flatten, zip);

/**
 * Percent-encode template params
 *
 * @example
 *
 *     const query = 'a/b ? b/a'
 *     const path = uri`/v1/search/${query}?format=json`;
 *     // path = "/v1/search/a%2Fb%20%3F%20b%2Fa?format=json"
 */
const uri = (templateStrings: ReadonlyArray<string>, ...params) => {
  const [s, ...strings] = templateStrings;
  return [s, ...interlace(params.map(encodeURIComponent), strings)].join('');
};

interface ApiErrorBody {
  error?: string;
  code?: string;
  version?: string;
}

abstract class ApiError extends Error {
  status: number;
  error?: string;
  code?: string;
  version?: string;

  constructor(status: number, body: ApiErrorBody) {
    const { error, code, version } = body;
    super(error);
    this.status = status;
    this.error = error;
    this.code = code;
    this.version = version;
  }
}

class SearchApiError extends ApiError {
  get name() {
    return 'SearchApiError';
  }

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
  }
}

class UploadTooLargeFileError extends ApiError {
  get name() {
    return 'UploadTooLargeFileError';
  }

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.message =
      'The photo is too large. Try to upload a photo less than 20mb.';
  }
}

class WrongMimeTypeFileError extends ApiError {
  get name() {
    return 'WrongMimeTypeFileError';
  }

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.message = 'The photo has wrong mime type.';
  }
}

class InternalServerError extends ApiError {
  get name() {
    return 'InternalServerError';
  }

  constructor(status: number, body: ApiErrorBody) {
    super(status, body);
    this.message = 'Internal server error';
  }
}

class SendEditSuggestionError extends Error {
  get name() {
    return 'SendEditSuggestionError';
  }

  /**
   *
   * @param {string} message
   */
  constructor(message) {
    super(message);
  }
}

class SendUserSatisfactionFeedbackError extends Error {
  get name() {
    return 'SendUserSatisfactionFeedbackError';
  }

  /**
   *
   * @param {string} message
   */
  constructor(message) {
    super(message);
  }
}

class GetPoiCategoriesError extends Error {
  get name() {
    return 'GetPoiCategoriesError';
  }

  /**
   *
   * @param {string} message
   */
  constructor(message) {
    super(message);
  }
}

async function searchApiRequest({
  path,
  search,
  ...params
}): Promise<GeocodingFeatureCollection> {
  const searchString = new URLSearchParams(clean(search)).toString();
  let url = `https://api.mapbox.com/search${path}`;
  if (searchString) {
    url += '?' + searchString;
  }
  const response = await fetch(url, params);

  if (!response.ok) {
    const body = await response.text();
    let parsedBody: ApiErrorBody;
    try {
      parsedBody = JSON.parse(body);
    } catch (error) {
      if ((error as Error).name === 'SyntaxError') {
        parsedBody = { error: body };
      } else {
        throw error;
      }
    }
    throw new SearchApiError(response.status, parsedBody);
  }

  return response.json();
}

export const forwardSearch = async ({ query, proximity }) => {
  return searchApiRequest({
    path: uri`/v1/forward/${query}`,
    search: {
      access_token: config.SEARCH_FEDERATION_ACCESS_TOKEN,
      language: 'en',
      limit: 10,
      proximity,
    },
  });
};

export const reverseSearch = async ({
  lng,
  lat,
  types,
  language = 'en',
  abortSignal,
}: {
  lng;
  lat;
  types;
  language?: string;
  abortSignal?;
}) => {
  const body = await searchApiRequest({
    path: uri`/v1/reverse/${lng},${lat}`,
    search: {
      access_token: config.SEARCH_FEDERATION_ACCESS_TOKEN,
      language,
      limit: 1,
      types,
    },
    signal: abortSignal,
  });

  const { features } = body;
  if (features.length) {
    return features[0];
  }
  return null;
};

export const sendEditSuggestion = async (suggestionType, payload) => {
  const suggestionResponse = await fetch(
    `${config.CONTRIBUTE_API_URL}/edit-suggestion/${suggestionType}?access_token=${config.CONTRIBUTE_API_ACCESS_TOKEN}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'content-type': 'application/json' },
    },
  );

  if (suggestionResponse.ok) {
    return suggestionResponse.json();
  }

  throw new SendEditSuggestionError(
    `${suggestionResponse.status} ${suggestionResponse.statusText}`,
  );
};

export const sendUserSatisfactionFeedback = async (payload) => {
  const userSatisfactionFeedbackResponse = await fetch(
    `${config.CONTRIBUTE_PRIVATE_API_URL}/user-satisfaction-feedback?access_token=${config.CONTRIBUTE_API_ACCESS_TOKEN}`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'content-type': 'application/json' },
    },
  );

  if (userSatisfactionFeedbackResponse.ok) {
    return userSatisfactionFeedbackResponse.json();
  }

  throw new SendUserSatisfactionFeedbackError(
    `${userSatisfactionFeedbackResponse.status} ${userSatisfactionFeedbackResponse.statusText}`,
  );
};

/**
 * @param {object} payload
 * @param {string} payload.fileData base64-encoded data url with mime type
 * @returns {Promise<{ message: string, status: string, results: { fileId: string } }>}
 */
export async function uploadPhoto(file) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(
    `${config.CONTRIBUTE_API_URL}/file/upload?access_token=${config.CONTRIBUTE_API_ACCESS_TOKEN}`,
    {
      method: 'POST',
      body: formData,
    },
  );

  if (response.ok) {
    return response.json();
  } else {
    const body = await response.text();
    let parsedBody;
    try {
      parsedBody = JSON.parse(body);
    } catch (error) {
      if ((error as Error).name === 'SyntaxError') {
        parsedBody = { error: body };
      } else {
        throw error;
      }
    }
    switch (parsedBody.statusCode) {
      case 413:
        throw new UploadTooLargeFileError(response.status, parsedBody);
      case 415:
        throw new WrongMimeTypeFileError(response.status, parsedBody);
      default:
        throw new InternalServerError(response.status, parsedBody);
    }
  }
}

export interface PoiCategoryDto {
  canonicalId: string;
  name: string;
}

export const getPoiCategories = async (): Promise<PoiCategoryDto[]> => {
  const poiCategoriesResponse = await fetch(
    `${config.CONTRIBUTE_PRIVATE_API_URL}/poi-categories?access_token=${config.CONTRIBUTE_API_ACCESS_TOKEN}`,
  );

  if (poiCategoriesResponse.ok) {
    return poiCategoriesResponse.json();
  }

  throw new GetPoiCategoriesError(
    `${poiCategoriesResponse.status} ${poiCategoriesResponse.statusText}`,
  );
};

export const apiClient = {
  reverseSearch,
  sendEditSuggestion,
  sendUserSatisfactionFeedback,
  uploadPhoto,
  forwardSearch,
  getPoiCategories,
};
